import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Minus, Plus, ShoppingBag, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { api, imgSrc, money } from "../lib/api";
import { useCart, countLetters } from "../context/CartContext";

export const QtyPicker = ({ value, onChange, testid }) => (
  <div className="inline-flex items-center rounded-full border-2 border-white/20" data-testid={testid}>
    <button className="p-3 hover:text-[var(--pink)]" onClick={() => onChange(value - 1)} data-testid={`${testid}-minus`} aria-label="Decrease"><Minus className="h-4 w-4" /></button>
    <span className="w-8 text-center font-extrabold" data-testid={`${testid}-value`}>{value}</span>
    <button className="p-3 hover:text-[var(--pink)]" onClick={() => onChange(value + 1)} data-testid={`${testid}-plus`} aria-label="Increase"><Plus className="h-4 w-4" /></button>
  </div>
);

function BoxOptions({ p, size, setSize, text, setText, addons, setAddons, flavors, setFlavors }) {
  const letters = countLetters(text);
  return (
    <div className="panel space-y-5 p-5" data-testid="product-options">
      {p.sizes?.length > 0 && (
        <div>
          <p className="mb-2 text-sm font-extrabold text-white/80">Size</p>
          <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap">
            {p.sizes.map((s) => (
              <button key={s.label} type="button" onClick={() => setSize(s.label)}
                data-testid={`size-option-${s.label.replace(/\s+/g, "-").toLowerCase()}`}
                className={`rounded-2xl border-2 px-5 py-3 text-left transition-colors ${size === s.label ? "border-[var(--pink)] bg-[#EC3D8C]/15" : "border-white/20 hover:border-[var(--blue-soft)]"}`}>
                <span className="block font-display text-lg">{s.label}</span>
                <span className="text-sm text-[var(--pink)]">{money(s.price)}{p.per_letter ? " per letter" : ""}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {p.per_letter && (
        <label className="block space-y-2">
          <span className="text-sm font-extrabold text-white/80">Letters / word to spell</span>
          <input className="field font-display text-xl uppercase tracking-widest" maxLength={40} placeholder="e.g. MOM, LOVE, M, 30"
            value={text} onChange={(e) => setText(e.target.value)} data-testid="product-spell-input" />
          <span className="block text-xs text-white/55" data-testid="product-letter-count">
            {letters} {letters === 1 ? "letter" : "letters"} · spaces don't count · up to {MAX_LETTERS} letters
          </span>
        </label>
      )}
      {p.flavors?.length > 0 && (
        <div data-testid="flavor-picker">
          <p className="mb-2 text-sm font-extrabold text-white/80">{p.flavor_picks > 1 ? `Choose your ${p.flavor_picks} flavors` : "Flavor"}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {flavors.map((f, i) => (
              <select key={i} className="field" value={f} onChange={(e) => setFlavors(flavors.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`flavor-select-${i}`}>
                <option value="">{p.flavor_picks > 1 ? `Flavor ${i + 1}…` : "Choose a flavor…"}</option>
                {p.flavors.map((o) => <option key={o}>{o}</option>)}
              </select>
            ))}
          </div>
        </div>
      )}
      {p.addons?.length > 0 && (
        <div>
          <p className="mb-2 text-sm font-extrabold text-white/80">Add-ons (per box)</p>
          <div className="grid gap-3 sm:flex sm:flex-wrap">
            {p.addons.map((a) => {
              const on = addons.includes(a.label);
              return (
                <button key={a.label} type="button" aria-pressed={on}
                  onClick={() => setAddons(on ? addons.filter((x) => x !== a.label) : [...addons, a.label])}
                  data-testid={`addon-option-${a.label.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`flex items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left transition-colors ${on ? "border-[var(--blue-soft)] bg-[#1F4FD8]/20" : "border-white/20 hover:border-[var(--pink)]"}`}>
                  <span className={`flex h-5 w-5 items-center justify-center rounded-md border-2 ${on ? "border-[var(--blue-soft)] bg-[var(--blue)]" : "border-white/40"}`}>{on && <Check className="h-3.5 w-3.5" />}</span>
                  <span><span className="block font-bold">{a.label}</span><span className="text-sm text-[var(--pink)]">+{money(a.price)}</span></span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const MAX_LETTERS = 20;

function useIsMobile() {
  const q = "(max-width: 639px)";
  const [m, setM] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = () => setM(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return m;
}

function BuyBar({ children }) {
  const mobile = useIsMobile();
  const bar = (
    <div data-testid="product-buy-bar"
      className={mobile ? "fixed inset-x-0 bottom-0 z-30 flex items-center gap-2 border-t border-white/10 bg-[var(--ink)]/95 px-3 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur-xl" : "flex flex-wrap items-center gap-4"}>
      {children}
    </div>
  );
  return mobile ? createPortal(bar, document.body) : bar;
}

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { add } = useCart();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [size, setSize] = useState(null);
  const [text, setText] = useState("");
  const [addons, setAddons] = useState([]);
  const [flavors, setFlavors] = useState([]);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => { setP(r.data); setSize(r.data.sizes?.[0]?.label || null); setFlavors(Array(r.data.flavors?.length ? r.data.flavor_picks || 1 : 0).fill("")); }).catch(() => setMissing(true));
  }, [id]);

  if (missing) return <div className="mx-auto max-w-3xl px-4 py-24 text-center" data-testid="product-not-found"><h1 className="text-4xl">Treat not found</h1><Link to="/shop" className="btn-candy mt-8">Back to shop</Link></div>;
  if (!p) return <div className="mx-auto max-w-6xl px-4 py-24"><div className="panel h-96 animate-pulse" /></div>;

  const letters = countLetters(text);
  const unit = (p.sizes?.find((s) => s.label === size)?.price ?? p.price) * (p.per_letter ? Math.max(letters, 1) : 1)
    + (p.addons || []).filter((a) => addons.includes(a.label)).reduce((s, a) => s + a.price, 0);
  const onAdd = (go) => {
    if (p.per_letter && (letters < 1 || letters > MAX_LETTERS)) return toast.error(`Please enter 1 to ${MAX_LETTERS} letters to spell`);
    if (flavors.some((f) => !f)) return toast.error(p.flavor_picks > 1 ? `Please choose all ${p.flavor_picks} flavors` : "Please choose a flavor");
    add(p, qty, { size, text, addons, flavors });
    toast.success(`${qty} × ${p.name}${size ? ` (${size})` : ""}${text ? ` "${text.trim().toUpperCase()}"` : ""}${flavors.length ? ` · ${flavors.join(", ")}` : ""}${addons.length ? ` + ${addons.join(" + ")}` : ""} added to cart`);
    if (go) nav("/cart");
  };
  const hasOptions = p.sizes?.length > 0 || p.per_letter || p.addons?.length > 0 || p.flavors?.length > 0;

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-12" data-testid="product-detail-page">
      <Link to="/shop" className="mb-5 inline-flex items-center gap-2 py-2 text-white/70 hover:text-[var(--pink)] sm:mb-8" data-testid="back-to-shop-link"><ArrowLeft className="h-4 w-4" /> Back to shop</Link>
      <div className="grid gap-8 sm:gap-12 lg:grid-cols-2">
        <div className="rise self-start overflow-hidden rounded-[2rem] border-4 border-white shadow-[0_0_0_6px_var(--pink),0_0_0_12px_var(--blue)]">
          <img src={imgSrc(p.image_url)} alt={p.name} className="aspect-square w-full object-cover" data-testid="product-detail-image" />
        </div>
        <div className="rise flex flex-col gap-6" style={{ animationDelay: "120ms" }}>
          <span className="self-start rounded-full bg-[var(--blue)] px-4 py-1 text-sm font-extrabold">{p.category}</span>
          <h1 className="text-4xl sm:text-5xl" data-testid="product-detail-name">{p.name}</h1>
          <p className="font-display text-4xl text-[var(--pink)]" data-testid="product-detail-price">
            {money(hasOptions ? unit : p.price)}
            {p.per_letter && <span className="ml-2 align-middle font-sans text-sm font-bold text-white/60">{letters > 0 ? `for ${letters} ${letters === 1 ? "letter" : "letters"}` : "per letter"}</span>}
          </p>
          <p className="leading-relaxed text-white/75" data-testid="product-detail-description">{p.description}</p>
          {hasOptions && <BoxOptions p={p} size={size} setSize={setSize} text={text} setText={setText} addons={addons} setAddons={setAddons} flavors={flavors} setFlavors={setFlavors} />}
          <BuyBar>
            <QtyPicker value={qty} onChange={(v) => setQty(Math.max(1, Math.min(50, v)))} testid="product-qty" />
            <button className="btn-royal flex-1 whitespace-nowrap !px-3 sm:flex-none sm:!px-7" onClick={() => onAdd(false)} data-testid="product-add-to-cart-button"><Plus className="h-5 w-5" /> <span className="sm:hidden">Add {money((hasOptions ? unit : p.price) * qty)}</span><span className="hidden sm:inline">Add to cart</span></button>
            <button className="btn-candy !px-4 sm:!px-7" onClick={() => onAdd(true)} data-testid="product-buy-now-button" aria-label="Buy now"><ShoppingBag className="h-5 w-5" /> <span className="hidden sm:inline">Buy now</span></button>
          </BuyBar>
          <p className="font-script text-lg text-[var(--blue-soft)]">Want it in your colors? <Link to="/custom-order" className="text-[var(--pink)] underline" data-testid="product-custom-link">Request a custom order</Link></p>
        </div>
      </div>
    </div>
  );
}
